import axios from 'axios'
import { useEffect, useState } from 'react'
import { useAuth } from './useAuth'
import { useWorkspace } from './useWorkspace'

const roleMap: Record<string, string[]> = {
  dashboard: ['dashboard member'],
  insights: ['insights member', 'insights manager'],
  journeys: ['journeys member', 'journey manager'],
  projects: ['projects member', 'project manager'],
  'risk assessments': ['risk assessments member', 'risk manager'],
  'daily labour': ['daily labour member'],
  engagement: ['engagement member', 'engagement manager'],
  communities: ['communities member', 'communities manager'],
  'incident management': ['incident management member', 'incident manager'],
}

const domainMap: Record<string, string> = {
  insights: 'insights',
  journeys: 'journeys',
  projects: 'projects',
  'risk assessments': 'risks',
  'daily labour': 'labours',
  engagement: 'engagement',
  communities: 'communities',
  'incident management': 'incident_m',
}

const normalize = (s?: string) => (s ?? '')
  .toLowerCase()
  .normalize('NFKC')
  .replace(/[\u00A0\u1680\u180E\u2000-\u200B\u202F\u205F\u3000]/g, ' ')
  .trim()
  .replace(/\s+/g, ' ')

// ——— tiny helper kept in this file ———
type RoleRow = {
  domain_id: string | number;
  type?: string;
  roles?: Array<string | { type?: string }>;
}

async function fetchCurrentUserDomainRoles(
  userId: number | string,
  domainId: number | string,
): Promise<string[]> {
  const { data } = await axios.post('/api/v1/domain/get_roles', {
    id: userId,
    domain_id: domainId, // ask backend to filter if supported
  })

  const rows: RoleRow[] = Array.isArray(data?.roledata) ? data.roledata : []

  // client-side guarantee
  const rowsForDomain = rows.filter((r) => String(r.domain_id) === String(domainId))

  const raw = rowsForDomain.flatMap((r) => {
    if (Array.isArray(r.roles) && r.roles.length) {
      return r.roles.map((x) => (typeof x === 'string' ? x : x?.type))
    }
    return r.type ? [r.type] : []
  })

  // unique + normalized
  return Array.from(new Set(raw.filter(Boolean).map(normalize)))
}

const useAppAccess = () => {
  const auth = useAuth()
  const workspaceHook = useWorkspace()

  // roles for *current user & current domain* (normalized strings)
  const [domainRoles, setDomainRoles] = useState<string[]>([])

  useEffect(() => {
    if (!auth.user?.user_id || !workspaceHook.workspace?.id) return;

    (async () => {
      try {
        const rolesForThisUserAndDomain = await fetchCurrentUserDomainRoles(
          auth.user.user_id,
          workspaceHook.workspace.id,
        )
        setDomainRoles(rolesForThisUserAndDomain)
      } catch (err) {
        console.error('Failed to fetch roles for current user/domain', err)
        setDomainRoles([]) // safe fallback
      }
    })()
  }, [auth.user?.user_id, workspaceHook.workspace?.id])

  const canAccess = (app: string): boolean => {
    const normalizedRoles = new Set(domainRoles.map(normalize))

    const hasUniversalRole = ['administrator', 'manager', 'member'].some((role) => normalizedRoles.has(role))

    if (hasUniversalRole) return true

    const roleAllowed = roleMap[app]?.some((r) => normalizedRoles.has(normalize(r)))
    return Boolean(roleAllowed)
  }

  return canAccess
}

export default useAppAccess
